import { FALLBACK_TEXT, formatAmountReport, formatNumber } from '@/utils';
import type z from 'zod';
import type { ReportFundBusinessElementModel } from '../../models';

type Format1ValueConverterParams = {
  fundBusiness: z.infer<typeof ReportFundBusinessElementModel> | undefined;
  type: 'csv' | 'pdf';
  isDebugMode: boolean;
};

export const getCountByType = ({
  value,
  type,
}: {
  value: number | null | undefined;
  type: 'csv' | 'pdf';
}): number | null | string => {
  if (type === 'csv') {
    return typeof value === 'number' ? value : null;
  }
  // PDFは１つのセルに複数の値を表示するため、nullやundefinedはフォールバックテキストを表示する
  if (value === null || value === undefined) {
    return FALLBACK_TEXT;
  }
  return formatNumber(value);
};

export const fundBusinessValueConverter = ({
  fundBusiness,
  type,
  isDebugMode,
}: Format1ValueConverterParams) => {
  return {
    //// 一昨年の支援等に関する実績
    // 決定額
    investmentDecisionAmount2YearsBefore: formatAmountReport(
      fundBusiness?.investmentDecisionAmount2YearsBefore,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentDecisionAmount2YearsBeforeCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentDecisionAmount2YearsBefore
            : undefined,
      },
    ),
    investmentDecisionCount2YearsBefore: getCountByType({
      value: fundBusiness?.investmentDecisionCount2YearsBefore,
      type,
    }),
    // 実行額
    investmentExecutionAmount2YearsBefore: formatAmountReport(
      fundBusiness?.investmentExecutionAmount2YearsBefore,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentExecutionAmount2YearsBeforeCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentExecutionAmount2YearsBefore
            : undefined,
      },
    ),
    investmentExecutionCount2YearsBefore: getCountByType({
      value: fundBusiness?.investmentExecutionCount2YearsBefore,
      type,
    }),
    // 前年の支援等に関する実績
    // 決定額
    investmentDecisionAmount1YearBefore: formatAmountReport(
      fundBusiness?.investmentDecisionAmount1YearBefore,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentDecisionAmount1YearBeforeCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentDecisionAmount1YearBefore
            : undefined,
      },
    ),
    investmentDecisionCount1YearBefore: getCountByType({
      value: fundBusiness?.investmentDecisionCount1YearBefore,
      type,
    }),
    // 実行額
    investmentExecutionAmount1YearBefore: formatAmountReport(
      fundBusiness?.investmentExecutionAmount1YearBefore,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentExecutionAmount1YearBeforeCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentExecutionAmount1YearBefore
            : undefined,
      },
    ),
    investmentExecutionCount1YearBefore: getCountByType({
      value: fundBusiness?.investmentExecutionCount1YearBefore,
      type,
    }),
    // 当年度の支援等に関する実績見込み
    // 決定額
    investmentDecisionAmountCurrentYear: formatAmountReport(
      fundBusiness?.investmentDecisionAmountCurrentYear,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentDecisionAmountCurrentYearCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentDecisionAmountCurrentYear
            : undefined,
      },
    ),
    investmentDecisionCountCurrentYear: getCountByType({
      value: fundBusiness?.investmentDecisionCountCurrentYear,
      type,
    }),
    // 実行額
    investmentExecutionAmountCurrentYear: formatAmountReport(
      fundBusiness?.investmentExecutionAmountCurrentYear,
      type,
      {
        isDebugMode,
        negativeCount:
          fundBusiness?.negativeInvestmentExecutionAmountCurrentYearCount,
        rawValue:
          type === 'pdf'
            ? fundBusiness?.rawInvestmentExecutionAmountCurrentYear
            : undefined,
      },
    ),
    investmentExecutionCountCurrentYear: getCountByType({
      value: fundBusiness?.investmentExecutionCountCurrentYear,
      type,
    }),
  };
};
